import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { AdminNav } from "@/components/admin/AdminNav";

type Range = 7 | 30 | 90;

type PostRow = {
  id: string;
  title: string;
  slug: string;
  status: string;
  published_at: string | null;
};
type ToolRow = {
  tool: string;
  score: number | null;
  email: string | null;
  cta_clicked: string | null;
  created_at: string;
};
type SubRow = { source: string | null; status: string; created_at: string };
type ContactRow = { created_at: string };

export const Route = createFileRoute("/admin/analytics")({
  head: () => ({
    meta: [
      { title: "Admin | Analytics | Scott Cunningham" },
      { name: "description", content: "Analytics for Scott Cunningham’s website administration." },
      { property: "og:title", content: "Admin | Analytics | Scott Cunningham" },
      { property: "og:description", content: "Analytics for Scott Cunningham’s website administration." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin | Analytics | Scott Cunningham" },
      { name: "twitter:description", content: "Analytics for Scott Cunningham’s website administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminAnalyticsPage,
});

function AdminAnalyticsPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>(30);
  const [posts, setPosts] = useState<PostRow[]>([]);
  const [tools, setTools] = useState<ToolRow[]>([]);
  const [subs, setSubs] = useState<SubRow[]>([]);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    setBusy(true);
    const since = new Date(Date.now() - range * 86400_000).toISOString();
    Promise.all([
      supabase
        .from("posts")
        .select("id, title, slug, status, published_at")
        .order("published_at", { ascending: false }),
      supabase
        .from("tool_submissions")
        .select("tool, score, email, cta_clicked, created_at")
        .gte("created_at", since),
      supabase
        .from("newsletter_signups")
        .select("source, status, created_at")
        .gte("created_at", since),
      supabase.from("contact_submissions").select("created_at").gte("created_at", since),
    ]).then(([p, t, s, c]) => {
      setPosts((p.data as PostRow[]) ?? []);
      setTools((t.data as ToolRow[]) ?? []);
      setSubs((s.data as SubRow[]) ?? []);
      setContacts((c.data as ContactRow[]) ?? []);
      setBusy(false);
    });
  }, [isAdmin, range]);

  const stats = useMemo(() => {
    const published = posts.filter((p) => p.status === "published");
    const draft = posts.filter((p) => p.status === "draft").length;
    const newPosts = published.filter(
      (p) => p.published_at && new Date(p.published_at).getTime() > Date.now() - range * 86400_000,
    ).length;

    const toolByName = new Map<string, number>();
    let toolEmails = 0;
    let ctaClicks = 0;
    let scoreSum = 0;
    let scoreCount = 0;
    for (const t of tools) {
      toolByName.set(t.tool, (toolByName.get(t.tool) ?? 0) + 1);
      if (t.email) toolEmails += 1;
      if (t.cta_clicked) ctaClicks += 1;
      if (typeof t.score === "number") {
        scoreSum += t.score;
        scoreCount += 1;
      }
    }
    const topTools = [...toolByName.entries()].sort((a, b) => b[1] - a[1]);

    const sourceMap = new Map<string, number>();
    let activeSubs = 0;
    for (const s of subs) {
      const src = s.source || "direct";
      sourceMap.set(src, (sourceMap.get(src) ?? 0) + 1);
      if (s.status === "active") activeSubs += 1;
    }
    const topSources = [...sourceMap.entries()].sort((a, b) => b[1] - a[1]);

    const toolConv = tools.length ? Math.round((toolEmails / tools.length) * 100) : 0;
    const avgScore = scoreCount ? Math.round(scoreSum / scoreCount) : null;

    return {
      publishedCount: published.length,
      draft,
      newPosts,
      toolRuns: tools.length,
      toolEmails,
      toolConv,
      ctaClicks,
      avgScore,
      topTools,
      subs: subs.length,
      activeSubs,
      topSources,
      contacts: contacts.length,
    };
  }, [posts, tools, subs, contacts, range]);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/login" });
  };

  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user) return null;
  if (!isAdmin) return <div className="p-12 text-muted-foreground">No admin access.</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <AdminNav
        title="Analytics"
        actions={
          <Button variant="outline" onClick={signOut}>
            Sign out
          </Button>
        }
      />

      <div className="mt-6 inline-flex rounded-md border border-border p-1">
        {[7, 30, 90].map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r as Range)}
            className={`rounded px-3 py-1 text-xs font-mono uppercase tracking-[0.15em] ${
              range === r
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Last {r}d
          </button>
        ))}
      </div>

      {busy ? (
        <p className="mt-8 text-muted-foreground">Loading metrics…</p>
      ) : (
        <>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              label="Published posts"
              value={stats.publishedCount}
              sub={`${stats.newPosts} new · ${stats.draft} drafts`}
            />
            <Tile
              label="Tool runs"
              value={stats.toolRuns}
              sub={`${stats.toolEmails} emails · ${stats.toolConv}% conv.`}
            />
            <Tile
              label="Newsletter signups"
              value={stats.subs}
              sub={`${stats.activeSubs} active`}
            />
            <Tile
              label="Contact messages"
              value={stats.contacts}
              sub={`${stats.ctaClicks} tool CTA clicks`}
            />
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <Panel
              title="Top tools"
              empty={stats.topTools.length === 0 ? "No tool runs in this range." : null}
            >
              <ul className="divide-y divide-border">
                {stats.topTools.map(([name, count]) => (
                  <li key={name} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                      {name}
                    </span>
                    <span className="font-display text-lg">{count}</span>
                  </li>
                ))}
              </ul>
              {stats.avgScore !== null && (
                <p className="mt-3 text-xs text-muted-foreground">Avg score: {stats.avgScore}</p>
              )}
            </Panel>

            <Panel
              title="Subscriber sources"
              empty={stats.topSources.length === 0 ? "No signups in this range." : null}
            >
              <ul className="divide-y divide-border">
                {stats.topSources.map(([src, count]) => (
                  <li key={src} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                      {src}
                    </span>
                    <span className="font-display text-lg">{count}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          </div>

          <Panel title="Recent published posts" className="mt-8">
            {posts.filter((p) => p.status === "published").length === 0 ? (
              <p className="text-sm text-muted-foreground">No published posts.</p>
            ) : (
              <ul className="divide-y divide-border">
                {posts
                  .filter((p) => p.status === "published")
                  .slice(0, 10)
                  .map((p) => (
                    <li key={p.id} className="flex items-center justify-between py-2 text-sm">
                      <Link to="/blog/$slug" params={{ slug: p.slug }} className="hover:underline">
                        {p.title}
                      </Link>
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                        {p.published_at ? new Date(p.published_at).toLocaleDateString() : "N/A"}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </Panel>

          <p className="mt-8 text-xs text-muted-foreground">
            Traffic, referrers, and per-post pageviews aren't tracked in the app database. Check the
            hosting analytics dashboard for those.
          </p>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 font-display text-3xl">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Panel({
  title,
  children,
  empty,
  className,
}: {
  title: string;
  children: React.ReactNode;
  empty?: string | null;
  className?: string;
}) {
  return (
    <div className={`rounded-lg border border-border p-5 ${className ?? ""}`}>
      <h2 className="font-display text-xl">{title}</h2>
      <div className="mt-3">
        {empty ? <p className="text-sm text-muted-foreground">{empty}</p> : children}
      </div>
    </div>
  );
}
